import React from "react";

type ModulesConfigElem = {
	moduleName: string;
	moduleCategory?: string;
	moduleWeight?: number;
	moduleOrder?: number;
};

/*
	Возвращает массив процентов, рассчитанных из moduleWeight для тех элементов
	из appModulesConfig, которые соответствуют текущим panes.	

	Алгоритм:
		- извлекаем ключи из panes (React.key)
		- фильтруем appModulesConfig, оставляя только те записи, чей moduleName
			соответствует одному из ключей (точно, endsWith(`_${key}`) или includes(key))
		- сортируем отфильтрованные записи по moduleOrder asc
		- вычисляем проценты = weight / totalWeight * 100	
	Возвращаем массив процентов в том же порядке, в котором идут отфильтрованные и
	отсортированные записи. Если ничего не найдено — возвращаем пустой массив.
*/

const getModulesWeights = (
	appModulesConfig?: ModulesConfigElem[],
	panes?: React.ReactNode[],
	aliases?: Record<string, string[]>,
): number[] => {
	if (!appModulesConfig || appModulesConfig.length === 0) return [];
	if (!panes || panes.length === 0) return [];

	const paneKeys = panes
		.map((p) => String((p as React.ReactElement)?.key ?? "").trim())
		.filter(Boolean);
	if (paneKeys.length === 0) return [];

	const matchesKey = (
		moduleName: string,
		key: string,
		aliasList: string[] = [],
	) =>
		moduleName === key ||
		moduleName.endsWith(`_${key}`) ||
		moduleName.includes(key) ||
		aliasList.some((a) => moduleName.includes(a));

	// Оставляем из конфига только те записи, которые соответствуют paneKeys и имеют вес > 0
	const configsForPanes = paneKeys
		.map((k) => {
			const cfg = appModulesConfig.find(
				(c) =>
					c &&
					c.moduleName &&
					matchesKey(c.moduleName, k, aliases?.[k] ?? []) &&
					Number(c.moduleWeight ?? 0) !== 0,
			);
			return cfg ?? null;
		})
		.filter((c): c is ModulesConfigElem => c !== null);

	if (configsForPanes.length === 0) return [];

	// Суммируем веса (приводим к числам на случай, если вес пришёл как строка)
	const totalWeight = configsForPanes.reduce(
		(s, c) => s + Number(c.moduleWeight ?? 0),
		0,
	);
	if (totalWeight <= 0) {
		const equal = 100 / configsForPanes.length;
		return configsForPanes.map(() => Number(equal.toFixed(3)));
	}

	return configsForPanes.map((c) =>
		Number(((Number(c.moduleWeight ?? 0) / totalWeight) * 100).toFixed(3)),
	);
};

export { getModulesWeights };
